// Max Speed's shared pure computation — extracted out of MaxSpeed.js (2026-09-10) so
// MaxSpeedPanel.js (the Einstein compose panel) reuses the exact same personal-max/season-max
// logic instead of a second hand-copy. Same extraction pattern as gps/matchChronicShared.js.
import { getGPS, getPlayers, getMssSquad } from '../../api';
import { dateToSeason } from '../../SeasonContext';

// Exposure threshold, as a % of the player's MSS ceiling — mirrors gpsWeekSummary.js's
// TOP_SPEED_EXPOSURE_PCT (the source of truth; this page keeps its own copy per this codebase's
// existing convention). 93 rather than 95 because the max-speed drill is PRESCRIBED at 95%.
export const EXPOSURE_PCT = 93;

export function getSeasonStart() {
  const now = new Date();
  const year = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-08-01`;
}

// One player's max-speed summary from the shared team-wide sessions/mssByPlayer data —
// personalMax (mss_ceiling when the nightly estimator has produced one, else the raw observed
// maximum so a player is never blank before the first run), seasonMax, and the most recent record.
export function computeMaxSpeedSummary(playerId, sessions, mssByPlayer, selectedSeasons = []) {
  const playerRecords = (sessions || [])
    .flatMap(s =>
      (s.players || [])
        // !is_estimated (2026-10-24): an estimated (KNVB tracking) max speed never sets a personal
        // max, season max or "last record" here.
        .filter(p => String(p.player_id) === String(playerId) && Number(p.max_speed) > 0 && !p.is_estimated)
        .map(p => ({
          date: (s.session_date || '').slice(0, 10),
          title: s.session_title || s.title || '',
          mdLabel: p.md_label || '',
          maxSpeed: Number(p.max_speed),
          sessionId: s.id,
          playerId: String(p.player_id),
        }))
    )
    .filter(r => {
      if (!r.date) return false;
      const season = dateToSeason(r.date);
      return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
    })
    .sort((a, b) => b.date.localeCompare(a.date));

  const observedMax = playerRecords.length > 0 ? Math.max(...playerRecords.map(r => r.maxSpeed)) : null;
  const mssRow = mssByPlayer[String(playerId)] || null;
  const personalMax = mssRow && mssRow.mss_ceiling_kmh != null ? Number(mssRow.mss_ceiling_kmh) : observedMax;

  const cutoff = getSeasonStart();
  const seasonVals = playerRecords.filter(r => r.date >= cutoff).map(r => r.maxSpeed);
  const seasonMax = seasonVals.length > 0 ? Math.max(...seasonVals) : null;

  const lastRecord = playerRecords[0] || null;

  return { playerRecords, observedMax, personalMax, seasonMax, lastRecord };
}

// Shared reference-data fetch for MaxSpeedPanel.js — team-wide sessions/players/MSS ceilings,
// one fetch feeding N per-player panels, same pattern as fetchMatchChronicPanelData.
export async function fetchMaxSpeedPanelData() {
  const [sessions, players, mssRows] = await Promise.all([getGPS(), getPlayers(), getMssSquad()]);
  return {
    sessions: sessions || [],
    players: players || [],
    mssByPlayer: Object.fromEntries((mssRows || []).map(r => [String(r.player_id), r])),
  };
}
